import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  Tooltip,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { api } from "../api/client";
import type {
  AttentionItem,
  BusinessUnitStat,
  ModuleStat,
  ProjectRunSummary,
} from "../api/types";
import type { InsightDocument } from "../api/insights";
import { ReadinessPanel, planningBasisText } from "./cockpit/views";
import {
  ADVANCED_MODULES,
  FOUNDATION_MODULES,
  MODULE_DISPLAY_ORDER,
  RISK_LEVEL_ORDER,
  TECHNICAL_EXPOSURE,
  formatPct,
  formatScore,
  moduleLabel,
  moduleShortLabel,
  moduleVocabulary,
  orderModules,
  riskLevelColor,
  riskLevelLabel,
  riskLevelTint,
} from "../lib/riskVocabulary";

/**
 * Portfolio Dashboard — Overview tab.
 *
 * Sprint 1 rewrite (defects D1–D4, D11). Every number here is read from the
 * run summary API, which computes it from the engine's own results:
 *   - scores are the engine's 0–100 scores, never ordinals of risk levels;
 *   - a mean is coloured by that module's OWN configured band (mean_band),
 *     never by a client-side threshold table;
 *   - headlines count assets at High or above, because a mean hides tails.
 * Labels, colours and order come from lib/riskVocabulary.ts, which is
 * byte-identical to the backend copy the printable reports use, so screen and
 * PDF cannot disagree.
 *
 * Step 2 (C-001…C-011): Overall risk is the primary panel and plots all
 * seven dimensions with the weights the run used (summary.composite_weights);
 * Baseline risk (business exposure) and Technical exposure are secondary
 * views. "Assets needing attention" is Overall risk High or above; data gaps
 * are listed separately. Not applicable and data gap are reported apart.
 */

type CompositeCode = "integrated_foundation" | "integrated_advanced";

const COMPOSITE_ACCENT: Record<CompositeCode, string> = {
  integrated_foundation: "#185FA5",
  integrated_advanced: "#A32D2D",
};
const EXPOSURE_ACCENT = "#5b4a9e";

function statOf(s: ProjectRunSummary, code: string): ModuleStat | undefined {
  return s.module_stats.find((m) => m.module_code === code);
}

function levelSegments(s: ProjectRunSummary, code: string) {
  return RISK_LEVEL_ORDER.map((level) => ({
    level,
    count: s.risk_distribution
      .filter((d) => d.module_code === code && d.risk_level === level)
      .reduce((a, d) => a + d.count, 0),
  })).filter((x) => x.count > 0);
}

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n.toLocaleString()} ${n === 1 ? one : many}`;
}

const pct = (w: number) => `${Math.round(w * 1000) / 10}%`;

/** One row per dimension of a composite: its weight in this run and its mean score. */
interface DimensionRow {
  code: string;
  weight: number | null;
  varies: boolean;
  stat: ModuleStat | undefined;
}

/**
 * Dimensions of a composite with the weights the run actually used (summary
 * composite_weights), heaviest first, ties in canonical order. When the run
 * recorded no weights, the canonical dimension list is shown without weights.
 */
function dimensionRows(s: ProjectRunSummary, composite: CompositeCode, fallback: readonly string[]): DimensionRow[] {
  const recorded = s.composite_weights?.[composite] ?? [];
  const order = (c: string) => {
    const i = (MODULE_DISPLAY_ORDER as readonly string[]).indexOf(c);
    return i < 0 ? 99 : i;
  };
  if (recorded.length === 0) {
    return fallback.map((code) => ({ code, weight: null, varies: false, stat: statOf(s, code) }));
  }
  return [...recorded]
    .sort((a, b) => b.weight - a.weight || order(a.module_code) - order(b.module_code))
    .map((w) => ({ code: w.module_code, weight: w.weight, varies: w.varies, stat: statOf(s, w.module_code) }));
}

function weightSentence(rows: DimensionRow[]): string {
  const weighted = rows.filter((r) => r.weight !== null);
  if (weighted.length === 0) return "This run did not record the dimension weights.";
  const total = weighted.reduce((a, r) => a + (r.weight ?? 0), 0);
  const list = weighted.map((r) => `${moduleLabel(r.code)} ${pct(r.weight! / (total || 1))}`).join(" · ");
  const varies = weighted.some((r) => r.varies) ? " Some business units use their own weights; the most common are shown." : "";
  return `Weights in this run: ${list}. Each asset's score is re-weighted over the dimensions that apply to it.${varies}`;
}

export function PortfolioDashboard({ projectId }: { projectId: string }) {
  const q = useQuery({
    queryKey: ["project-run-summary", projectId],
    queryFn: () =>
      api.get<ProjectRunSummary>(`/projects/${projectId}/runs/latest/summary`),
  });
  // The same insight document the cockpit above reads (shared cache): it holds
  // the Technical exposure view and Organisational readiness.
  const insights = useQuery({
    queryKey: ["project-insights", projectId],
    queryFn: () => api.get<InsightDocument>(`/projects/${projectId}/insights/latest`),
    enabled: !!q.data,
    retry: false,
  });
  // Overall by default: priority, the cockpit KPIs and every report count High+ on Overall risk.
  const [buComposite, setBuComposite] = useState<CompositeCode>(
    "integrated_advanced",
  );

  if (q.isLoading)
    return (
      <div className="p-8 text-ink-500 text-sm italic">Loading overview…</div>
    );
  if (q.isError || !q.data) {
    return (
      <div className="p-12 text-center">
        <p className="text-ink-600 font-medium mb-1">
          No completed assessment yet
        </p>
        <p className="text-sm text-ink-400">
          Add assets, then click Run assessment.
        </p>
      </div>
    );
  }

  const s = q.data;
  const baseline = statOf(s, "integrated_foundation");
  const overall = statOf(s, "integrated_advanced");
  const ev = s.evidence;
  const crqc = s.assumptions?.crqc ?? null;
  const modulesInRun = orderModules(s.module_stats.map((m) => m.module_code));
  const overallRows = dimensionRows(s, "integrated_advanced", [...FOUNDATION_MODULES, ...ADVANCED_MODULES]);
  const baselineRows = dimensionRows(s, "integrated_foundation", FOUNDATION_MODULES);
  const doc = insights.data;
  const dataGaps = s.data_gaps ?? [];
  const dataGapsTotal = s.data_gaps_total ?? 0;
  const buRows = s.bu_stats
    .filter((b) => b.module_code === buComposite)
    .sort(
      (a, b) =>
        b.high_plus / Math.max(1, b.scored) -
          a.high_plus / Math.max(1, a.scored) ||
        a.business_unit_name.localeCompare(b.business_unit_name),
    );

  return (
    <div className="p-6 space-y-8 max-w-6xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-xs text-ink-500 font-mono">
          Last run:{" "}
          {s.completed_at ? new Date(s.completed_at).toLocaleString() : "—"} ·{" "}
          {plural(s.asset_count, "asset")}
        </p>
        <div className="flex items-center gap-2">
          {s.run_id && (
            <a
              href={`/runs/${s.run_id}/results`}
              className="text-xs text-accent hover:underline border border-accent/30 rounded-sm px-3 py-1.5"
            >
              View full results →
            </a>
          )}
        </div>
      </div>

      {/* Summary */}
      <section aria-label="Summary">
        <Headline summary={s} baseline={baseline} overall={overall} />
        <div className="grid grid-cols-4 gap-3 mt-3">
          <Kpi value={s.asset_count.toLocaleString()} label="Assets in this run" />
          <Kpi
            value={overall ? overall.high_plus.toLocaleString() : "—"}
            label={`High or above · ${moduleShortLabel("integrated_advanced")}`}
            sub={
              overall
                ? `${formatPct(overall.high_plus, overall.scored)} of ${plural(overall.scored, "scored asset")}`
                : "not in this run"
            }
            color={riskLevelColor("Very_high")}
          />
          <Kpi
            value={baseline ? baseline.high_plus.toLocaleString() : "—"}
            label={`High or above · ${moduleShortLabel("integrated_foundation")}`}
            sub={
              baseline
                ? `${formatPct(baseline.high_plus, baseline.scored)} of ${plural(baseline.scored, "scored asset")}`
                : "not in this run"
            }
            color={riskLevelColor("High")}
          />
          <Kpi
            value={ev ? formatPct(ev.inputs_evidenced, ev.inputs_total) : "—"}
            label="Evidence coverage"
            sub={
              ev
                ? `${ev.inputs_evidenced} of ${ev.inputs_total} inputs captured · ${ev.inputs_defaulted} defaulted`
                : "not reported for this run"
            }
          />
        </div>
        {(doc?.planning_year || crqc) && (
          <p className="text-xs text-ink-500 mt-2" data-testid="crqc-assumption">
            {doc?.planning_year ? (
              <>
                <span title="The year this assessment assumes a cryptographically relevant quantum computer (CRQC) could exist">CRQC planning year</span> <strong className="text-ink-700">{doc.planning_year.year}</strong> ({planningBasisText(doc.planning_year)}), used by the Timeline dimension and by every asset's out-of-time tests.
                {doc.planning_year.legacy_scores && (
                  <> Dimension scores as scored on {doc.planning_year.legacy_scores.scored_on ?? "the run date"}{doc.planning_year.legacy_scores.timeline_year !== null && doc.planning_year.legacy_scores.timeline_year !== doc.planning_year.year ? `: the Timeline dimension used ${doc.planning_year.legacy_scores.timeline_year}` : ""}; re-run the assessment to score them against {doc.planning_year.year}.</>
                )}
              </>
            ) : crqc ? (
              <>
                <span title="The year this assessment assumes a cryptographically relevant quantum computer (CRQC) could exist">CRQC planning year</span> <strong className="text-ink-700">{crqc.effective_target_year}</strong> (
                {crqc.basis === "project_planning_horizon"
                  ? "fixed for this project"
                  : crqc.basis === "crqc_probability_table"
                    ? `first year the expert curve reaches ${crqc.threshold_pct}%`
                    : "default; the expert curve is empty"}
                ), as scored.
              </>
            ) : null}
          </p>
        )}
      </section>

      {/* Overall risk — the primary view, all seven dimensions with their weights */}
      <CompositePanel
        summary={s}
        code="integrated_advanced"
        rows={overallRows}
        testId="panel-overall"
      />

      {/* Secondary views */}
      <section aria-label="Secondary views">
        <SectionHeader
          title="Secondary views"
          subtitle="Two partial composites that explain Overall risk. Priority is set by Overall risk, not by these."
        />
        <div className="grid grid-cols-2 gap-4">
          <SecondaryComposite summary={s} code="integrated_foundation" rows={baselineRows} />
          <TechnicalExposurePanel doc={doc} loading={insights.isLoading} overallRows={overallRows} />
        </div>
      </section>

      {/* Organisational readiness */}
      <section aria-label="Organisational readiness" data-testid="stats-readiness">
        <SectionHeader
          title="Organisational readiness"
          subtitle="Gap assessment: 25 items in 6 categories, rated 1 (not started) to 4 (fully in place). Not part of the asset scores."
        />
        <div className="bg-white border border-ink-100 rounded-sm p-4">
          {doc ? (
            <ReadinessPanel readiness={doc.readiness} />
          ) : (
            <p className="text-xs text-ink-500">{insights.isLoading ? "Loading…" : "Not available for this run."}</p>
          )}
        </div>
      </section>

      {/* Distribution by dimension */}
      <section>
        <SectionHeader
          title="Risk by dimension"
          subtitle="Mean score (0–100) of scored assets, coloured by the dimension's own band · assets at High or above · assets not scored, split into not applicable and data gap"
        />
        <div className="bg-white border border-ink-100 rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-ink-500 border-b border-ink-100">
                <th className="text-left font-medium px-3 py-2">Dimension</th>
                <th className="text-left font-medium px-3 py-2 w-40">Mean score</th>
                <th className="text-right font-medium px-3 py-2 w-36">High or above</th>
                <th className="text-right font-medium px-3 py-2 w-28">Not applicable</th>
                <th className="text-right font-medium px-3 py-2 w-24">Data gap</th>
                <th className="text-left font-medium px-3 py-2">Scored assets by risk level</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-50">
              {modulesInRun.map((code) => {
                const st = statOf(s, code)!;
                return (
                  <tr key={code} data-testid={`dimension-row-${code}`}>
                    <td className="px-3 py-2 font-medium text-ink-800">
                      {moduleLabel(code)}
                    </td>
                    <td className="px-3 py-2">
                      <ScoreBar score={st.mean_score} band={st.mean_band} />
                    </td>
                    <td className="px-3 py-2 text-right font-mono" title={`${st.high_plus} of ${st.scored} scored assets`}>
                      <span className="font-semibold">{st.high_plus}</span>{" "}
                      <span className="text-ink-400 text-xs">of {st.scored} scored</span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-xs text-ink-500" title="The dimension does not apply to these assets (for example Supply Chain for an asset that is not vendor-supplied)">
                      {st.na_not_applicable}
                    </td>
                    <td className={`px-3 py-2 text-right font-mono text-xs ${st.na_data_gap > 0 ? "text-risk-high font-semibold" : "text-ink-500"}`} title="The dimension applies but could not be scored for lack of data">
                      {st.na_data_gap}
                    </td>
                    <td className="px-3 py-2">
                      <StackedBar segments={levelSegments(s, code).filter((x) => x.level !== "NA")} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <LevelLegend />
        </div>
      </section>

      {/* Business units */}
      <section>
        <div className="flex items-end justify-between mb-3">
          <SectionHeader
            title="Business units"
            subtitle="Sorted by share of scored assets at High or above"
            flush
          />
          <div className="flex text-xs border border-ink-200 rounded-sm overflow-hidden" role="group" aria-label="Composite">
            {(["integrated_advanced", "integrated_foundation"] as const).map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setBuComposite(c)}
                aria-pressed={buComposite === c}
                className={`px-3 py-1.5 ${buComposite === c ? "bg-ink-900 text-white" : "bg-white text-ink-600 hover:bg-ink-50"}`}
              >
                {moduleLabel(c)}
              </button>
            ))}
          </div>
        </div>
        <BusinessUnitTable rows={buRows} />
      </section>

      {/* Needs attention: Overall risk High or above */}
      <section data-testid="needs-attention">
        <SectionHeader
          title="Assets needing attention"
          subtitle={`${plural(s.needs_attention_total, "asset")} at High or above on ${moduleLabel("integrated_advanced")}${s.needs_attention_total > s.needs_attention.length ? ` · top ${s.needs_attention.length} shown` : ""} · highest score first`}
        />
        {s.needs_attention.length === 0 ? (
          <NoData label="No asset is at High or above on Overall risk in this run." />
        ) : (
          <AttentionList items={s.needs_attention} />
        )}
      </section>

      {/* Data gaps, listed separately */}
      <section data-testid="data-gaps">
        <SectionHeader
          title="Data gaps"
          subtitle={`${plural(dataGapsTotal, "asset")} with a dimension that applies but could not be scored for lack of data, or with no input values captured${dataGapsTotal > dataGaps.length ? ` · ${dataGaps.length} shown` : ""}`}
        />
        {dataGaps.length === 0 ? (
          <NoData label="No data gaps in this run." />
        ) : (
          <AttentionList items={dataGaps} gapsOnly />
        )}
      </section>
    </div>
  );
}

function AttentionList({ items, gapsOnly = false }: { items: AttentionItem[]; gapsOnly?: boolean }) {
  return (
    <div className="bg-white border border-ink-100 rounded-sm divide-y divide-ink-50">
      {items.map((a) => (
        <div
          key={a.asset_id}
          className="grid grid-cols-[1fr_160px_150px_1.4fr] gap-3 px-3 py-2 items-center text-sm"
        >
          <span className="font-medium text-ink-800 truncate">{a.asset_name}</span>
          <span className="text-xs text-ink-500 truncate">{a.business_unit_name}</span>
          <LevelBadge level={a.composite_risk_level} score={a.composite_score} />
          <span className="text-xs text-ink-600">
            {a.reasons
              .filter((r) => !(gapsOnly && r === "high_risk"))
              .map((r) => (
                <span key={r} className="block">
                  {r === "high_risk" && `${moduleLabel("integrated_advanced")} ${riskLevelLabel(a.composite_risk_level)}`}
                  {r === "data_gap" &&
                    `Not assessed for lack of data: ${a.data_gap_modules.map(moduleLabel).join(", ")}`}
                  {r === "no_evidence" && "No input values captured — defaults only"}
                </span>
              ))}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── Sections ────────────────────────────────────────────────────────────────

function Headline({
  summary,
  baseline,
  overall,
}: {
  summary: ProjectRunSummary;
  baseline: ModuleStat | undefined;
  overall: ModuleStat | undefined;
}) {
  const parts: string[] = [];
  if (overall && overall.scored > 0) {
    parts.push(
      `${overall.high_plus} of ${plural(overall.scored, "scored asset")} (${formatPct(overall.high_plus, overall.scored)}) ${overall.high_plus === 1 ? "is" : "are"} at High or above on ${moduleLabel("integrated_advanced")}${overall.na_data_gap > 0 ? `; ${plural(overall.na_data_gap, "asset")} could not be scored for lack of data` : ""}.`,
    );
  }
  if (baseline && baseline.scored > 0) {
    parts.push(
      `On ${moduleLabel("integrated_foundation")}, ${baseline.high_plus} of ${plural(baseline.scored, "scored asset")} ${baseline.high_plus === 1 ? "is" : "are"} High or above.`,
    );
  }
  const gaps = summary.data_gaps_total ?? 0;
  if (gaps > 0) {
    parts.push(`${plural(gaps, "asset")} ${gaps === 1 ? "has" : "have"} data gaps (listed below).`);
  }
  if (parts.length === 0) return null;
  return (
    <div
      className="border-l-4 border-ink-900 bg-ink-50 px-4 py-3 text-sm text-ink-800 leading-relaxed"
      data-testid="summary-headline"
    >
      {parts.join(" ")}
    </div>
  );
}

function radarData(rows: DimensionRow[]) {
  return rows
    .filter((r) => r.stat && r.stat.mean_score !== null)
    .map((r) => ({
      module: r.weight !== null ? `${moduleLabel(r.code)} ${pct(r.weight)}` : moduleLabel(r.code),
      score: r.stat!.mean_score!,
    }));
}

function DimensionRadar({ rows, accent, height = 220 }: { rows: DimensionRow[]; accent: string; height?: number }) {
  const radar = radarData(rows);
  if (radar.length < 3) {
    return (
      <p className="text-xs text-ink-500 py-6">
        {radar.map((r) => `${r.module}: ${formatScore(r.score)}`).join(" · ") || "No scored dimensions"}
      </p>
    );
  }
  return (
    <ResponsiveContainer width="100%" height={height}>
      <RadarChart data={radar} outerRadius="62%" margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
        <PolarGrid stroke="#e8e6e0" />
        <PolarAngleAxis dataKey="module" tick={{ fontSize: 10, fill: "#666" }} />
        <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
        <Radar dataKey="score" stroke={accent} fill={accent} fillOpacity={0.15} strokeWidth={1.5} />
        <Tooltip formatter={(val: number) => [`${formatScore(val)} / 100`, "Mean score"]} contentStyle={{ fontSize: 11 }} />
      </RadarChart>
    </ResponsiveContainer>
  );
}

function WeightTable({ rows }: { rows: DimensionRow[] }) {
  return (
    <table className="w-full text-xs" data-testid="weight-table">
      <thead>
        <tr className="text-[10px] uppercase tracking-wider text-ink-500 border-b border-ink-100">
          <th className="text-left font-medium py-1">Dimension</th>
          <th className="text-right font-medium py-1">Weight</th>
          <th className="text-right font-medium py-1">Mean</th>
          <th className="text-right font-medium py-1">Scored</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-ink-50">
        {rows.map((r) => (
          <tr key={r.code}>
            <td className="py-1 text-ink-800">{moduleLabel(r.code)}</td>
            <td className="py-1 text-right font-mono whitespace-nowrap">{r.weight !== null ? `${pct(r.weight)}${r.varies ? "*" : ""}` : "—"}</td>
            <td className="py-1 text-right font-mono" style={{ color: r.stat?.mean_band ? riskLevelColor(r.stat.mean_band) : undefined }}>
              {formatScore(r.stat?.mean_score ?? null)}
            </td>
            <td className="py-1 text-right font-mono text-ink-500 whitespace-nowrap">{r.stat ? `${r.stat.scored} of ${r.stat.total}` : "not in run"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function CompositePanel({
  summary,
  code,
  rows,
  testId,
}: {
  summary: ProjectRunSummary;
  code: CompositeCode;
  rows: DimensionRow[];
  testId: string;
}) {
  const v = moduleVocabulary(code)!;
  const stat = statOf(summary, code);
  const accent = COMPOSITE_ACCENT[code];
  const donut = levelSegments(summary, code).filter((d) => d.level !== "NA");
  const total = donut.reduce((a, d) => a + d.count, 0);

  return (
    <section data-testid={testId}>
      <SectionHeader title={v.label} subtitle={v.description} color={accent} />
      {!stat || stat.total === 0 ? (
        <NoData label={`${v.label} is not part of this run.`} />
      ) : (
        <>
          <div className="grid grid-cols-[190px_minmax(0,1fr)_270px] gap-4">
            <div className="bg-white border border-ink-100 rounded-sm p-4 flex flex-col items-center justify-center">
              <p className="text-xs font-medium text-ink-500 uppercase tracking-wide mb-1">
                {v.short} — mean score
              </p>
              <ScoreGauge score={stat.mean_score} band={stat.mean_band} />
              <p className="text-xs text-ink-400 mt-1 text-center">
                {stat.scored} of {stat.total} scored ·{" "}
                <span className="font-semibold" style={{ color: riskLevelColor("High") }}>
                  {stat.high_plus} High or above
                </span>
              </p>
            </div>
            <div className="bg-white border border-ink-100 rounded-sm p-4">
              <p className="text-sm font-medium text-ink-700 mb-1">All {rows.length} dimensions and their weights</p>
              <p className="text-xs text-ink-400 mb-2">Mean score of scored assets, 0–100 · axis label = weight</p>
              <div className="grid grid-cols-2 gap-3 items-center">
                <DimensionRadar rows={rows} accent={accent} />
                <WeightTable rows={rows} />
              </div>
            </div>
            <LevelDonut segments={donut} total={total} />
          </div>
          <p className="text-xs text-ink-500 mt-2" data-testid={`${testId}-weights`}>{weightSentence(rows)}</p>
        </>
      )}
    </section>
  );
}

function SecondaryComposite({ summary, code, rows }: { summary: ProjectRunSummary; code: CompositeCode; rows: DimensionRow[] }) {
  const v = moduleVocabulary(code)!;
  const stat = statOf(summary, code);
  const accent = COMPOSITE_ACCENT[code];
  return (
    <div className="bg-white border border-ink-100 rounded-sm p-4" data-testid="panel-baseline">
      <h3 className="text-sm font-semibold" style={{ color: accent }}>{v.label}</h3>
      <p className="text-xs text-ink-500 mb-2">{v.description}</p>
      {!stat || stat.total === 0 ? (
        <p className="text-xs text-ink-500">Not part of this run.</p>
      ) : (
        <>
          <p className="text-xs text-ink-700">
            Mean <LevelBadge level={stat.mean_band} score={stat.mean_score} /> · {stat.high_plus} of {stat.scored} scored High or above
          </p>
          <DimensionRadar rows={rows} accent={accent} height={180} />
          <p className="text-[11px] text-ink-500">{weightSentence(rows)}</p>
        </>
      )}
    </div>
  );
}

function TechnicalExposurePanel({ doc, loading, overallRows }: { doc: InsightDocument | undefined; loading: boolean; overallRows: DimensionRow[] }) {
  const rows = overallRows.filter((r) => (ADVANCED_MODULES as readonly string[]).includes(r.code));
  const scored = doc ? doc.views.points.filter((p) => p.exposure !== null) : [];
  const high = doc ? doc.views.overlap.both + doc.views.overlap.exposure_only : 0;
  const mean = scored.length > 0 ? scored.reduce((a, p) => a + (p.exposure ?? 0), 0) / scored.length : null;
  return (
    <div className="bg-white border border-ink-100 rounded-sm p-4" data-testid="panel-exposure">
      <h3 className="text-sm font-semibold" style={{ color: EXPOSURE_ACCENT }}>{TECHNICAL_EXPOSURE.label}</h3>
      <p className="text-xs text-ink-500 mb-2">{TECHNICAL_EXPOSURE.description}</p>
      {doc ? (
        <p className="text-xs text-ink-700">
          Mean <span className="font-mono">{formatScore(mean)}</span> · {high} of {plural(scored.length, "scored asset")} High or above
        </p>
      ) : (
        <p className="text-xs text-ink-500">{loading ? "Loading…" : "The asset counts are not available for this run."}</p>
      )}
      <DimensionRadar rows={rows} accent={EXPOSURE_ACCENT} height={180} />
      <p className="text-[11px] text-ink-500">
        {doc && doc.views.exposure_weights
          ? `Weights in this view: ${Object.entries(doc.views.exposure_weights).map(([c, w]) => `${moduleLabel(c)} ${pct(w)}`).join(" · ")} (the Overall risk weights, re-weighted to sum to 100%).`
          : "Uses the Overall risk weights of Threat, Cryptography and Compliance, re-weighted to sum to 100%."}
      </p>
    </div>
  );
}

function LevelDonut({ segments, total }: { segments: Array<{ level: string; count: number }>; total: number }) {
  return (
    <div className="bg-white border border-ink-100 rounded-sm p-4">
      <p className="text-sm font-medium text-ink-700 mb-1">Scored assets by risk level</p>
      <p className="text-xs text-ink-400 mb-2">{plural(total, "scored asset")}</p>
      <div className="flex items-center gap-3">
        <ResponsiveContainer width={120} height={120}>
          <PieChart>
            <Pie data={segments} dataKey="count" nameKey="level" cx="50%" cy="50%" innerRadius={36} outerRadius={56} paddingAngle={2}>
              {segments.map((d) => (
                <Cell key={d.level} fill={riskLevelColor(d.level)} />
              ))}
            </Pie>
            <Tooltip
              formatter={(val: number, name: string) => [`${val} (${formatPct(val, total)})`, riskLevelLabel(name)]}
              contentStyle={{ fontSize: 10 }}
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="space-y-1 flex-1 min-w-0">
          {segments.map((d) => (
            <div key={d.level} className="flex items-center justify-between gap-1 text-xs">
              <span className="flex items-center gap-1.5 truncate">
                <span className="w-2 h-2 rounded-sm flex-shrink-0" style={{ background: riskLevelColor(d.level) }} />
                <span className="text-ink-600 truncate">{riskLevelLabel(d.level)}</span>
              </span>
              <span className="font-mono text-ink-700 font-medium">{d.count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function BusinessUnitTable({ rows }: { rows: BusinessUnitStat[] }) {
  if (rows.length === 0) return <NoData label="No business-unit results for this composite." />;
  return (
    <div className="bg-white border border-ink-100 rounded-sm overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-ink-500 border-b border-ink-100">
            <th className="text-left font-medium px-3 py-2">Business unit</th>
            <th className="text-right font-medium px-3 py-2 w-20">Assets</th>
            <th className="text-right font-medium px-3 py-2 w-28">High or above</th>
            <th className="text-left font-medium px-3 py-2 w-36">Mean score</th>
            <th className="text-left font-medium px-3 py-2">Assets by risk level</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-ink-50">
          {rows.map((b) => (
            <tr key={`${b.module_code}-${b.business_unit_id}`}>
              <td className="px-3 py-2 font-medium text-ink-800">{b.business_unit_name}</td>
              <td className="px-3 py-2 text-right font-mono">{b.total}</td>
              <td className="px-3 py-2 text-right font-mono">
                <span className="font-semibold">{b.high_plus}</span>{" "}
                <span className="text-ink-400 text-xs">{formatPct(b.high_plus, b.scored)}</span>
              </td>
              <td className="px-3 py-2">
                <LevelBadge level={b.mean_band} score={b.mean_score} />
              </td>
              <td className="px-3 py-2">
                <StackedBar
                  segments={RISK_LEVEL_ORDER.filter((l) => (b.level_counts[l] ?? 0) > 0).map((l) => ({
                    level: l,
                    count: b.level_counts[l]!,
                  }))}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Primitives ──────────────────────────────────────────────────────────────

function SectionHeader({
  title,
  subtitle,
  color = "#0B1F3A",
  flush = false,
}: {
  title: string;
  subtitle: string;
  color?: string;
  flush?: boolean;
}) {
  return (
    <div className={`flex items-start gap-3 ${flush ? "" : "mb-3"}`}>
      <div className="w-1 h-8 rounded-full flex-shrink-0" style={{ background: color }} />
      <div>
        <h2 className="text-base font-display font-semibold" style={{ color }}>
          {title}
        </h2>
        <p className="text-xs text-ink-500">{subtitle}</p>
      </div>
    </div>
  );
}

function Kpi({ value, label, sub, color }: { value: string; label: string; sub?: string; color?: string }) {
  return (
    <div className="bg-white border border-ink-100 rounded-sm p-4">
      <div className="text-2xl font-semibold" style={color ? { color } : undefined}>
        {value}
      </div>
      <div className="text-[10px] uppercase tracking-wider text-ink-500 mt-1">{label}</div>
      {sub && <div className="text-xs text-ink-400 mt-1">{sub}</div>}
    </div>
  );
}

/** Semicircular gauge on the engine's 0–100 scale, coloured by the module's own band. */
function ScoreGauge({ score, band }: { score: number | null; band: string | null }) {
  if (score === null) return <p className="text-sm text-ink-400 py-6">Not scored</p>;
  const color = band ? riskLevelColor(band) : "#64748b";
  const clamped = Math.min(100, Math.max(0, score));
  const angle = (clamped / 100) * 180;
  const cx = 90, cy = 78, r = 60;
  const toRad = (d: number) => (d - 180) * (Math.PI / 180);
  const nx = cx + r * Math.cos(toRad(angle));
  const ny = cy + r * Math.sin(toRad(angle));
  return (
    <svg width="180" height="104" viewBox="0 0 180 104" role="img" aria-label={`Mean score ${formatScore(clamped)} of 100, ${band ? riskLevelLabel(band) : "no band"}`}>
      <path d="M30,78 A60,60 0 0,1 150,78" fill="none" stroke="#e8e6e0" strokeWidth="9" strokeLinecap="round" />
      {clamped > 0 && (
        <path d={`M30,78 A60,60 0 0,1 ${nx},${ny}`} fill="none" stroke={color} strokeWidth="9" strokeLinecap="round" />
      )}
      <text x={cx} y={cy - 8} textAnchor="middle" fontSize="24" fontWeight="600" fill={color}>
        {formatScore(clamped)}
      </text>
      <text x={cx} y={cy + 8} textAnchor="middle" fontSize="10" fill="#888">
        mean score, 0–100
      </text>
      <text x={cx} y={cy + 24} textAnchor="middle" fontSize="11" fontWeight="700" fill={color}>
        {band ? riskLevelLabel(band) : "No band"}
      </text>
    </svg>
  );
}

function ScoreBar({ score, band }: { score: number | null; band: string | null }) {
  if (score === null) return <span className="text-xs text-ink-400">—</span>;
  const color = band ? riskLevelColor(band) : "#64748b";
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-ink-50 rounded-sm overflow-hidden">
        <div className="h-full" style={{ width: `${Math.min(100, Math.max(0, score))}%`, background: color }} />
      </div>
      <span className="font-mono text-xs w-9 text-right" style={{ color }}>
        {formatScore(score)}
      </span>
    </div>
  );
}

function StackedBar({ segments }: { segments: Array<{ level: string; count: number }> }) {
  const total = segments.reduce((a, s) => a + s.count, 0);
  if (total === 0) return <div className="h-3.5 bg-ink-50 rounded-sm" />;
  return (
    <div className="flex h-3.5 rounded-sm overflow-hidden ring-1 ring-ink-100">
      {segments.map((seg) => (
        <div
          key={seg.level}
          title={`${riskLevelLabel(seg.level)}: ${seg.count}`}
          style={{ width: `${(seg.count / total) * 100}%`, backgroundColor: riskLevelColor(seg.level) }}
        />
      ))}
    </div>
  );
}

function LevelBadge({ level, score }: { level: string | null; score: number | null }) {
  if (!level) return <span className="text-xs text-ink-400">{formatScore(score)}</span>;
  const color = riskLevelColor(level);
  return (
    <span
      className="inline-block text-xs font-semibold px-2 py-0.5 rounded-sm"
      style={{ color, background: riskLevelTint(level), border: `1px solid ${color}33` }}
    >
      {riskLevelLabel(level)}
      {score != null && <span className="opacity-70 font-normal ml-1">{formatScore(score)}</span>}
    </span>
  );
}

function LevelLegend() {
  return (
    <div className="flex flex-wrap gap-3 px-3 py-2 border-t border-ink-50">
      {RISK_LEVEL_ORDER.filter((l) => l !== "NA").map((l) => (
        <span key={l} className="flex items-center gap-1 text-[11px] text-ink-500">
          <span className="w-2.5 h-2.5 rounded-sm" style={{ background: riskLevelColor(l) }} />
          {riskLevelLabel(l)}
        </span>
      ))}
    </div>
  );
}

function NoData({ label }: { label: string }) {
  return (
    <div className="bg-ink-50/50 border border-ink-100 rounded-sm p-6 text-center">
      <p className="text-sm text-ink-400">{label}</p>
    </div>
  );
}
